import React, { Fragment } from 'react';
import {
  CheckboxOptions,
  FieldComponentTypes,
  FieldValueTypes,
  FormField,
  RadioOptions,
  SelectOptions,
  TypeaheadOptions,
} from '@dtp/membership-service-types';
import { FieldError, UseFormReturn } from 'react-hook-form';
import { Input, Message, Select } from '@naf/input';
import { Checkbox } from '@naf-simba/elements-checkbox';
import { RadioButton } from '@naf-simba/elements-radio-button';
import { LabelVariant, Toggle } from '@naf-simba/elements-toggle';
import xor from 'lodash/xor';
import { formatPhoneNumber, parsePhoneNumber } from '../../lib/phoneValidation';
import { PhoneInputWithCountryCode } from '../../shared/components/PhoneInputWithCountryCode';
import { normalizeInput } from '../../utils/normalizeInput';
import { get } from '../../utils/safeGet';
import BlockContent from '../block-content/BlockContent';
import { Email } from '../Email';
import { FileInput } from '../fileUpload/FileUpload';
import { PostalPlace } from '../PostalPlace';
import { Typeahead } from '../Typeahead';
import { VehicleSearchInput } from '../vehicleSearch';
import S from './Styles';

interface FormFieldInputProps {
  field: FormField;
  fieldId: string;
  value: any;
  onChange: (value: any) => void;
  isDisabled: boolean;
  errors: UseFormReturn['formState']['errors'];
  clearErrors?: UseFormReturn['clearErrors'];
  watch?: UseFormReturn['watch'];
  setValue?: UseFormReturn['setValue'];
  extraMargin: boolean;
}

export const FormFieldInput = ({
  field,
  fieldId,
  value,
  onChange,
  isDisabled,
  errors,
  clearErrors,
  watch,
  setValue,
  extraMargin,
}: FormFieldInputProps) => {
  const hasError = !!get(errors, fieldId);

  if (field.component === FieldComponentTypes.TextInput && field.id === 'postalPlace') {
    return (
      <PostalPlace
        id={field.id}
        disabled={isDisabled}
        error={hasError}
        value={value || ''}
        onChange={onChange}
        width={field.width}
        watch={watch}
        name={fieldId}
        setValue={setValue}
        clearErrors={clearErrors}
      />
    );
  }
  if (field.component === FieldComponentTypes.TextInput && field.id === 'email') {
    return (
      <Email
        id={field.id}
        disabled={isDisabled}
        error={hasError || !!errors.manualEmailError}
        value={value || ''}
        onChange={onChange}
        width={field.width}
        watch={watch}
        name={fieldId}
      />
    );
  }
  if (field.component === FieldComponentTypes.TextInput) {
    return (
      <S.StyledInput
        id={field.id}
        type={field.type === FieldValueTypes.Number ? 'number' : 'text'}
        disabled={isDisabled}
        error={hasError}
        value={value || ''}
        onChange={(event) => onChange(normalizeInput(event))}
        width={field.width}
      />
    );
  }
  if (field.component === FieldComponentTypes.Number) {
    return (
      <Input
        id={field.id}
        type={field.type === FieldValueTypes.Number ? 'number' : 'text'}
        disabled={isDisabled}
        error={hasError}
        value={value || ''}
        onChange={onChange}
      />
    );
  }
  if (field.component === FieldComponentTypes.TextArea) {
    return (
      <S.TextFieldWrapper>
        <Input
          id={field.id}
          type={field.type === FieldValueTypes.Number ? 'number' : 'text'}
          disabled={isDisabled}
          error={hasError}
          value={value || ''}
          onChange={onChange}
          placeholder={field.placeHolder}
          as="textarea"
          maxCharacters={500}
        />
      </S.TextFieldWrapper>
    );
  }
  if (field.component === FieldComponentTypes.PhoneNumber) {
    return (
      <PhoneInputWithCountryCode
        id={field.id}
        label=""
        placeholder={field.placeHolder}
        disabled={isDisabled}
        errorMessage={(get(errors, fieldId) as FieldError)?.message}
        value={parsePhoneNumber(typeof value === 'string' ? value : value?.number)}
        onChange={(next) => onChange(next.number ? formatPhoneNumber(next) : '')}
      />
    );
  }
  if (field.component === FieldComponentTypes.Typeahead) {
    const options = field.options as TypeaheadOptions;
    return (
      <Typeahead
        label={field.label}
        dataSource={options?.dataSource}
        valueKey={options?.valueKey}
        displayKey={options?.displayKey}
        error={hasError}
        onChange={onChange}
        placeholder={field.placeHolder}
        width={field.width}
        value={value}
        skipBearerToken
        setValue={setValue}
        id={field.id}
      />
    );
  }
  if (field.component === FieldComponentTypes.Toggle) {
    return (
      <S.ToggleWrapper>
        <Toggle
          disabled={isDisabled}
          name={field.label}
          onChange={({ value: toggleValue }: { value: boolean }) => onChange(toggleValue)}
          checked={value as boolean}
          labelOption={LabelVariant.YesNo}
        />
      </S.ToggleWrapper>
    );
  }
  if (field.component === FieldComponentTypes.Radio) {
    return (
      <S.RadioWrapper>
        {(field.options as RadioOptions)?.map((option) => {
          const selected = (value as unknown as boolean) === (option.value as unknown as boolean);
          return (
            <Fragment key={`${fieldId}-${option.value}`}>
              <RadioButton
                label={option.label}
                name={field.label}
                onChange={() => onChange(option.value)}
                checked={selected}
                disabled={isDisabled}
                error={hasError}
              />
              {selected && option.textIfSelected ? (
                <S.TextIfSelectedWrapper>
                  <BlockContent value={option.textIfSelected} />
                </S.TextIfSelectedWrapper>
              ) : null}
            </Fragment>
          );
        })}
        {hasError &&
          Object.keys(field.validation).map((key) => (
            <Message key={key} className="errorContainer" error>
              {field.validation[key]?.message}
            </Message>
          ))}
      </S.RadioWrapper>
    );
  }
  if (field.component === FieldComponentTypes.Select) {
    const options = field.options as SelectOptions;
    return (
      <Select
        label={field.label}
        options={Object.values(options.selectableValues)}
        name={field.label}
        handleSelect={(option: { label: string; value: string }) => onChange(option.value)}
        selected={options.selectableValues[field.initialValue as string]}
        disabled={isDisabled}
        error={hasError}
        width={options.width}
      />
    );
  }
  if (field.component === FieldComponentTypes.Checkbox) {
    return (
      <S.CheckboxWrapper>
        {(field.options as CheckboxOptions)?.selectableValues?.map((option) => (
          <Checkbox
            disabled={isDisabled}
            error={!!errors[field.id]}
            label={option.label}
            name={field.label}
            key={`${fieldId}-${option.value}`}
            onChange={() => onChange(xor(value as string[], [option.value]))}
          />
        ))}
      </S.CheckboxWrapper>
    );
  }
  if (field.component === FieldComponentTypes.RegistrationNumber) {
    return (
      <VehicleSearchInput
        error={!!errors[field.id]}
        onChange={onChange}
        disabled={isDisabled}
        placeholder={field.placeHolder}
      />
    );
  }
  if (field.component === FieldComponentTypes.FileUpload) {
    return (
      <FileInput
        disabled={isDisabled}
        error={!!errors[field.id]}
        name={field.id}
        onChange={onChange}
        extraMargin={extraMargin}
      />
    );
  }
  return null;
};
